import { Link, useParams } from 'react-router-dom';
import { GoalsSection } from '../components/goals/GoalsSection';
import { HexaSection } from '../components/goals/HexaSection';
import { LevelHistorySection } from '../components/goals/LevelHistorySection';
import { StarForceSection } from '../components/goals/StarForceSection';
import { SymbolsSection } from '../components/goals/SymbolsSection';
import { classDisplayName, useClasses } from '../lib/reference';
import { useAccounts } from '../lib/roster';

/** `/characters/:id` — one character's progression: symbols, star force, HEXA, goals, level history. */
export function CharacterDetail() {
  const { id } = useParams<{ id: string }>();
  const characterId = Number(id);
  const accountsQuery = useAccounts();
  const classesQuery = useClasses();

  if (accountsQuery.isPending || classesQuery.isPending) {
    return <p className="text-sm text-text-muted">Loading character…</p>;
  }
  if (accountsQuery.isError || classesQuery.isError) {
    return (
      <p role="alert" className="text-sm text-danger">
        Could not load this character. Please retry in a moment.
      </p>
    );
  }

  const character = (accountsQuery.data ?? [])
    .flatMap((account) => account.characters)
    .find((c) => c.id === characterId);

  if (!character || Number.isNaN(characterId)) {
    return (
      <div className="space-y-4">
        <p role="alert" className="text-sm text-danger">
          That character could not be found.
        </p>
        <Link
          to="/characters"
          className="inline-flex min-h-tap items-center text-sm font-medium text-accent hover:underline"
        >
          Back to Characters
        </Link>
      </div>
    );
  }

  const className = classDisplayName(character.classSlug, classesQuery.data);

  return (
    <div className="stagger-children space-y-5">
      <div>
        <Link
          to="/characters"
          className="inline-flex min-h-tap items-center text-sm font-semibold text-text-faint hover:text-accent"
        >
          ← Characters
        </Link>
        <div className="mt-2 flex items-center gap-3">
          <h1 className="font-display text-xl font-semibold text-text">{character.name}</h1>
          <span className="text-sm text-text-muted">
            {className} · Lv.{character.level}
          </span>
        </div>
      </div>

      <SymbolsSection characterId={character.id} characterLevel={character.level} />
      <StarForceSection characterId={character.id} />
      <HexaSection characterId={character.id} classSlug={character.classSlug} />
      <GoalsSection characterId={character.id} />
      <LevelHistorySection characterId={character.id} />
    </div>
  );
}
